import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { brl, formatApiError } from "@/lib/api";

export function CartDrawer() {
  const { items, open, setOpen, remove, setQty, totals, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("cart"); // cart | checkout | done
  const [form, setForm] = useState({ shipping_name: "", shipping_address: "", shipping_city: "" });
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [couponMsg, setCouponMsg] = useState("");

  const discount = applied?.discount || 0;
  const finalTotal = Math.max(0, totals.subtotal - discount) + totals.shipping;

  const close = () => {
    setOpen(false);
    setTimeout(() => setStep("cart"), 300);
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.get(`/coupons/validate?code=${encodeURIComponent(coupon)}&subtotal=${totals.subtotal}`);
      if (data.valid) { setApplied({ code: data.code, discount: data.discount }); setCouponMsg(data.message); }
      else { setApplied(null); setCouponMsg(data.message); }
    } catch { setApplied(null); setCouponMsg("Erro ao validar cupom"); }
  };

  const goCheckout = () => {
    if (!user) { setOpen(false); navigate("/login?redirect=checkout"); return; }
    setStep("checkout");
  };

  const placeOrder = async () => {
    if (!form.shipping_name || !form.shipping_address || !form.shipping_city) {
      toast.error("Preencha todos os campos de entrega."); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items, subtotal: totals.subtotal, shipping: totals.shipping, total: finalTotal,
        discount, coupon_code: applied?.code || null, ...form,
      });
      setOrder(data);
      setStep("done");
      clear();
      setApplied(null); setCoupon(""); setCouponMsg("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const progress = Math.min(100, (totals.subtotal / totals.freeShippingThreshold) * 100);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={close} className="fixed inset-0 z-[60] bg-forest-deep/50 backdrop-blur-sm" data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[61] flex h-full w-full max-w-md flex-col border-l border-borderlight bg-alabaster shadow-2xl"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-borderlight px-6 py-5">
              <h2 className="font-serif text-2xl text-forest-deep">
                {step === "done" ? "Pedido Confirmado" : step === "checkout" ? "Entrega" : "Sua Sacola"}
              </h2>
              <button onClick={close} data-testid="cart-close" className="text-ink hover:text-clay"><X size={22} /></button>
            </div>

            {step === "cart" && (
              <>
                {items.length === 0 ? (
                  <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                    <p className="font-serif text-2xl text-ink">Sua sacola está vazia</p>
                    <button onClick={() => { close(); navigate("/loja"); }} data-testid="cart-empty-shop"
                      className="bg-forest-deep px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-alabaster hover:bg-clay">
                      Explorar Coleção
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="border-b border-borderlight px-6 py-4">
                      {totals.freeShippingRemaining > 0 ? (
                        <p className="font-mono text-[11px] uppercase tracking-widest text-ink">
                          Faltam <span className="text-clay">{brl(totals.freeShippingRemaining)}</span> para frete grátis
                        </p>
                      ) : (
                        <p className="font-mono text-[11px] uppercase tracking-widest text-clay">✦ Frete grátis desbloqueado</p>
                      )}
                      <div className="mt-2 h-1 w-full bg-sand-elevated">
                        <div className="h-full bg-clay transition-all duration-500" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                    <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
                      {items.map((it) => (
                        <div key={it.product_id} className="flex gap-4" data-testid={`cart-item-${it.product_id}`}>
                          <img src={it.image} alt={it.name} className="h-24 w-20 flex-shrink-0 object-cover" />
                          <div className="flex flex-1 flex-col">
                            <p className="font-serif text-lg leading-tight text-ink">{it.name}</p>
                            <p className="font-sans text-sm text-clay">{brl(it.price)}</p>
                            <div className="mt-auto flex items-center justify-between">
                              <div className="flex items-center border border-borderlight">
                                <button onClick={() => setQty(it.product_id, it.quantity - 1)} data-testid={`cart-dec-${it.product_id}`} className="px-2 py-1 hover:text-clay"><Minus size={13} /></button>
                                <span className="px-3 font-mono text-xs">{it.quantity}</span>
                                <button onClick={() => setQty(it.product_id, it.quantity + 1)} data-testid={`cart-inc-${it.product_id}`} className="px-2 py-1 hover:text-clay"><Plus size={13} /></button>
                              </div>
                              <button onClick={() => remove(it.product_id)} data-testid={`cart-remove-${it.product_id}`} className="text-ink/50 hover:text-clay"><Trash2 size={16} /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-borderlight px-6 py-5">
                      <div className="mb-3">
                        <div className="flex gap-2">
                          <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Cupom de desconto" data-testid="coupon-input"
                            className="w-full border border-borderlight bg-white px-3 py-2.5 font-sans text-sm uppercase outline-none focus:border-clay" />
                          <button onClick={applyCoupon} data-testid="coupon-apply"
                            className="whitespace-nowrap bg-clay px-4 py-2.5 font-sans text-xs font-semibold uppercase tracking-wide text-white hover:opacity-90">Aplicar</button>
                        </div>
                        {couponMsg && <p data-testid="coupon-msg" className={`mt-1.5 font-sans text-xs ${applied ? "text-clay" : "text-red-600"}`}>{couponMsg}</p>}
                      </div>
                      <div className="mb-1 flex justify-between font-sans text-sm text-ink/70"><span>Subtotal</span><span>{brl(totals.subtotal)}</span></div>
                      {discount > 0 && <div className="mb-1 flex justify-between font-sans text-sm text-clay"><span>Desconto ({applied.code})</span><span>-{brl(discount)}</span></div>}
                      <div className="mb-3 flex justify-between font-sans text-sm text-ink/70"><span>Frete</span><span>{totals.shipping === 0 ? "Grátis" : brl(totals.shipping)}</span></div>
                      <div className="mb-4 flex justify-between font-serif text-2xl text-forest-deep"><span>Total</span><span data-testid="cart-total">{brl(finalTotal)}</span></div>
                      <button onClick={goCheckout} data-testid="checkout-btn"
                        className="w-full bg-forest-deep py-4 font-mono text-xs uppercase tracking-[0.2em] text-alabaster transition-colors hover:bg-clay">
                        Finalizar Compra
                      </button>
                    </div>
                  </>
                )}
              </>
            )}

            {step === "checkout" && (
              <div className="flex flex-1 flex-col overflow-y-auto px-6 py-6">
                <div className="space-y-4">
                  {[
                    { k: "shipping_name", l: "Nome completo", ph: "Maria Silva" },
                    { k: "shipping_address", l: "Endereço", ph: "Rua, número, complemento" },
                    { k: "shipping_city", l: "Cidade / UF", ph: "São Paulo, SP" },
                  ].map((f) => (
                    <div key={f.k}>
                      <label className="font-mono text-[10px] uppercase tracking-widest text-ink/60">{f.l}</label>
                      <input
                        value={form[f.k]} placeholder={f.ph}
                        onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                        data-testid={`checkout-${f.k}`}
                        className="mt-1 w-full border border-borderlight bg-white px-4 py-3 font-sans text-sm outline-none focus:border-clay"
                      />
                    </div>
                  ))}
                  <div className="border border-dashed border-borderlight bg-sand-elevated p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ink/60">Pagamento (demonstração)</p>
                    <p className="mt-1 font-sans text-sm text-ink">Checkout simulado — nenhum cartão é cobrado.</p>
                  </div>
                </div>
                <div className="mt-auto pt-6">
                  <div className="mb-4 flex justify-between font-serif text-2xl text-forest-deep"><span>Total</span><span>{brl(finalTotal)}</span></div>
                  <button onClick={placeOrder} disabled={busy} data-testid="place-order-btn"
                    className="w-full bg-clay py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#A3543A] disabled:opacity-50">
                    {busy ? "Processando..." : "Confirmar Pedido"}
                  </button>
                  <button onClick={() => setStep("cart")} className="mt-3 w-full py-2 font-mono text-[11px] uppercase tracking-widest text-ink/60 hover:text-clay">Voltar à sacola</button>
                </div>
              </div>
            )}

            {step === "done" && order && (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center" data-testid="order-success">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-forest-deep text-alabaster"><Check size={28} /></div>
                <h3 className="font-serif text-3xl text-forest-deep">Obrigado!</h3>
                <p className="font-sans text-sm text-ink/70">Seu pedido <span className="font-mono text-clay">{order.order_number}</span> foi confirmado e está sendo preparado com carinho.</p>
                <button onClick={() => { close(); navigate("/conta"); }} data-testid="view-orders-btn"
                  className="mt-2 bg-forest-deep px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-alabaster hover:bg-clay">
                  Ver Meus Pedidos
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
